import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';
import { inventoryService } from '../../services/inventoryService';
import { mealService } from '../../services/mealService';
import { userProfileService } from '../../services/userProfileService';

import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

// Helper functions to map between UI types and database enum values
const mapUITypeToDBType = (uiType) => {
  const mapping = {
    'Breakfast': 'Breakfast',
    'Lunch': 'Main Meal',
    'Dinner': 'Secondary Meal'
  };
  return mapping?.[uiType] || uiType;
};

const mapDBTypeToUIType = (dbType) => {
  const mapping = {
    'Breakfast': 'Breakfast',
    'Main Meal': 'Lunch',
    'Secondary Meal': 'Dinner'
  };
  return mapping?.[dbType] || dbType;
};

// PREDEFINED MEAL BANK - Only suggest meals when ALL ingredients are available
const MEAL_BANK = {
  breakfast: [
    { name: 'Scrambled eggs with cheese', ingredients: ['eggs', 'cheese'], servings: 4 },
    { name: 'Omelet', ingredients: ['eggs', 'onions', 'bell peppers', 'cheese'], servings: 4 },
    { name: 'Oatmeal', ingredients: ['oats', 'milk', 'sugar'], servings: 4 },
    { name: 'Yogurt with bananas', ingredients: ['yogurt', 'bananas'], servings: 4 },
    { name: 'Pancakes', ingredients: ['pancake mix', 'milk'], servings: 4 },
    { name: 'Eggs and toast', ingredients: ['eggs', 'bread'], servings: 4 },
    { name: 'Breakfast sandwich', ingredients: ['bread', 'eggs', 'cheese'], servings: 4 },
    { name: 'Oats with almond milk and sugar', ingredients: ['oats', 'almond milk', 'sugar'], servings: 4 },
    { name: 'Yogurt with fruit', ingredients: ['yogurt', 'fruit'], servings: 4 },
    { name: 'French toast', ingredients: ['bread', 'eggs', 'milk'], servings: 4 },
    { name: 'Farmers Omelet', ingredients: ['eggs', 'bell peppers', 'onions', 'cheese', 'ham'], servings: 4 },
    { name: 'Oatmeal (oats, milk, sugar)', ingredients: ['oats', 'milk', 'sugar'], servings: 4 },
    { name: 'Oats with almond milk + bananas', ingredients: ['oats', 'almond milk', 'bananas'], servings: 4 },
    { name: 'Pancakes (pancake mix, milk, fruit toppings (optional))', ingredients: ['pancake mix', 'milk'], servings: 4 },
    { name: 'Fruit salad', ingredients: ['fruit'], servings: 4 },
    { name: 'Mushroom Omelet', ingredients: ['eggs', 'mushrooms', 'cheese'], servings: 4 },
    { name: 'Potato hash', ingredients: ['sweet potatoes', 'onions', 'bell peppers'], servings: 4 },
    { name: 'Spinach and Feta Omelet', ingredients: ['eggs', 'spinach', 'feta cheese'], servings: 4 },
    { name: 'Sweet Potato Hash', ingredients: ['sweet potatoes', 'onions', 'bell peppers'], servings: 4 },
    { name: 'Banana Pancakes', ingredients: ['pancake mix', 'bananas', 'milk'], servings: 4 },
    { name: 'Yogurt Parfait', ingredients: ['yogurt', 'blueberries', 'granola'], servings: 4 },
    { name: 'Avocado Toast', ingredients: ['bread', 'avocado', 'lemon'], servings: 4 },
    { name: 'Mushroom and Cheese Toast', ingredients: ['bread', 'mushrooms', 'cheese'], servings: 4 },
    { name: 'Broccoli and Cheese Omelet', ingredients: ['eggs', 'broccoli', 'cheese'], servings: 4 },
    { name: 'Fruit Smoothie', ingredients: ['almond milk', 'strawberries', 'banana'], servings: 4 },
    { name: 'Kale and Egg Scramble', ingredients: ['eggs', 'kale', 'onion'], servings: 4 },
    { name: 'Apple Cinnamon Oatmeal', ingredients: ['oats', 'milk', 'apples', 'cinnamon'], servings: 4 },
    { name: 'Dragon Fruit Yogurt Bowl', ingredients: ['yogurt', 'dragon fruit', 'honey'], servings: 4 },
    { name: 'Zucchini Muffins', ingredients: ['zucchini', 'flour', 'sugar', 'eggs'], servings: 4 },
    { name: 'Pear and Almond Milk Oatmeal', ingredients: ['oats', 'almond milk', 'pear'], servings: 4 },
    { name: 'Sweet Potato and Egg Bowl', ingredients: ['sweet potatoes', 'eggs', 'butter'], servings: 4 },
    { name: 'Tomato and Cheese Omelet', ingredients: ['eggs', 'tomatoes', 'cheese'], servings: 4 },
    { name: 'Pineapple Yogurt Parfait', ingredients: ['yogurt', 'pineapple', 'granola'], servings: 4 },
    { name: 'Mixed Berry Oatmeal', ingredients: ['oats', 'milk', 'blueberries', 'raspberries', 'blackberries'], servings: 4 },
    { name: 'Veggie Frittata', ingredients: ['eggs', 'zucchini', 'bell peppers', 'onion'], servings: 4 },
    { name: 'Mango Smoothie', ingredients: ['almond milk', 'mango', 'banana'], servings: 4 },
    { name: 'Cauliflower Hash Browns', ingredients: ['cauliflower', 'eggs', 'cheese'], servings: 4 },
    { name: 'Avocado and Egg Toast', ingredients: ['bread', 'avocado', 'eggs'], servings: 4 },
    { name: 'Butternut Squash Breakfast Bowl', ingredients: ['butternut squash', 'eggs', 'cheese'], servings: 4 },
    { name: 'Cabbage and Egg Stir Fry', ingredients: ['cabbage', 'eggs', 'onions'], servings: 4 },
    { name: 'Fruit Salad with Citrus', ingredients: ['oranges', 'limes', 'mango', 'pineapple'], servings: 4 },
    { name: 'Papaya Yogurt Bowl', ingredients: ['papaya', 'yogurt', 'honey'], servings: 4 },
    { name: 'Raspberry Almond Oatmeal', ingredients: ['oats', 'almond milk', 'raspberries'], servings: 4 },
    { name: 'Tomato and Spinach Omelet', ingredients: ['eggs', 'tomatoes', 'spinach'], servings: 4 },
    { name: 'Sweet Potato and Kale Hash', ingredients: ['sweet potatoes', 'kale', 'onions'], servings: 4 },
    { name: 'Blueberry Pancakes', ingredients: ['pancake mix', 'blueberries', 'milk'], servings: 4 },
    { name: 'Bell Pepper and Cheese Breakfast Wrap', ingredients: ['tortilla', 'bell peppers', 'cheese', 'eggs'], servings: 4 },
    { name: 'Banana Oatmeal', ingredients: ['oats', 'milk', 'bananas', 'sugar'], servings: 4 },
    { name: 'Apple Cinnamon Oatmeal', ingredients: ['oats', 'milk', 'apples', 'sugar'], servings: 4 },
    { name: 'Blueberry Oatmeal', ingredients: ['oats', 'almond milk', 'blueberries'], servings: 4 },
    { name: 'Mixed Berry Yogurt Bowl', ingredients: ['yogurt', 'blueberries', 'raspberries', 'blackberries'], servings: 4 },
    { name: 'Mango Yogurt Parfait', ingredients: ['yogurt', 'mango', 'granola'], servings: 4 },
    { name: 'Pineapple Coconut Yogurt Bowl', ingredients: ['yogurt', 'pineapple', 'coconut'], servings: 4 },
    { name: 'Papaya Fruit Bowl', ingredients: ['papaya', 'lime', 'honey'], servings: 4 },
    { name: 'Fruit Salad with Honey', ingredients: ['mixed fruit', 'honey'], servings: 4 },
    { name: 'Strawberry Banana Smoothie', ingredients: ['almond milk', 'strawberries', 'bananas'], servings: 4 },
    { name: 'Mango Pineapple Smoothie', ingredients: ['almond milk', 'mango', 'pineapple'], servings: 4 },
    { name: 'Avocado Toast with Lemon', ingredients: ['bread', 'avocado', 'lemon'], servings: 4 },
    { name: 'Peanut Butter Toast', ingredients: ['bread', 'peanut butter', 'honey'], servings: 4 },
    { name: 'Nutella Toast', ingredients: ['bread', 'Nutella'], servings: 4 },
    { name: 'Cinnamon Sugar Toast', ingredients: ['bread', 'butter', 'sugar'], servings: 4 },
    { name: 'Granola with Milk', ingredients: ['granola', 'milk'], servings: 4 },
    { name: 'Granola with Yogurt', ingredients: ['granola', 'yogurt'], servings: 4 },
    { name: 'Oats with Almond Milk and Dates', ingredients: ['oats', 'almond milk', 'dates'], servings: 4 },
    { name: 'Raisin Cinnamon Oatmeal', ingredients: ['oats', 'milk', 'raisins'], servings: 4 },
    { name: 'Pancakes with Syrup', ingredients: ['pancake mix', 'milk', 'syrup'], servings: 4 },
    { name: 'Pancakes with Fruit Topping', ingredients: ['pancake mix', 'milk', 'fruit'], servings: 4 },
    { name: 'French Toast Without Egg', ingredients: ['bread', 'milk', 'sugar', 'cinnamon'], servings: 4 },
    { name: 'Yogurt with Honey and Nuts', ingredients: ['yogurt', 'honey', 'nuts'], servings: 4 },
    { name: 'Fruit and Cheese Plate', ingredients: ['cheese', 'apples', 'grapes'], servings: 4 },
    { name: 'Cottage Cheese with Fruit', ingredients: ['cottage cheese', 'pineapple'], servings: 4 },
    { name: 'Rice Porridge', ingredients: ['rice', 'milk', 'sugar'], servings: 4 },
    { name: 'Quinoa Breakfast Bowl', ingredients: ['quinoa', 'almond milk', 'fruit'], servings: 4 },
    { name: 'Sweet Couscous Bowl', ingredients: ['couscous', 'milk', 'sugar', 'raisins'], servings: 4 },
    { name: 'Toast with Jam and Butter', ingredients: ['bread', 'jelly', 'butter'], servings: 4 },
    { name: 'Apple Slices with Peanut Butter', ingredients: ['apples', 'peanut butter'], servings: 4 },
    { name: 'Chocolate Chip Oatmeal', ingredients: ['oats', 'milk', 'chocolate chips'], servings: 4 }
  ],
  lunch: [
    { name: 'Tuna sandwich', ingredients: ['canned tuna', 'bread', 'mayonnaise'], servings: 4 },
    { name: 'Ham and cheese sandwich', ingredients: ['ham', 'bread', 'cheese'], servings: 4 },
    { name: 'Chicken salad wrap', ingredients: ['chicken', 'lettuce', 'tortillas'], servings: 4 },
    { name: 'Egg salad sandwich', ingredients: ['eggs', 'mayonnaise', 'bread'], servings: 4 },
    { name: 'Grilled cheese', ingredients: ['bread', 'cheese', 'butter'], servings: 4 },
    { name: 'Tuna crackers', ingredients: ['canned tuna', 'crackers', 'mayonnaise'], servings: 4 },
    { name: 'Sardine toast', ingredients: ['sardines', 'bread', 'olive oil'], servings: 4 },
    { name: 'Veggie wrap', ingredients: ['lettuce', 'cucumbers', 'tortillas'], servings: 4 },
    { name: 'Chickpea garlic mash', ingredients: ['chickpeas', 'olive oil', 'garlic'], servings: 4 },
    { name: 'Chicken salad', ingredients: ['chicken', 'lettuce', 'mayonnaise'], servings: 4 },
    { name: 'Tuna salad', ingredients: ['tuna', 'lettuce', 'mayonnaise'], servings: 4 },
    { name: 'Chickpea salad', ingredients: ['chickpeas', 'cucumbers', 'olive oil'], servings: 4 },
    { name: 'Lentil salad', ingredients: ['lentils', 'onions', 'olive oil'], servings: 4 },
    { name: 'Cabbage slaw', ingredients: ['cabbage', 'carrots', 'mayonnaise'], servings: 4 },
    { name: 'Tomato cucumber salad', ingredients: ['tomatoes', 'cucumbers', 'olive oil', 'lettuce'], servings: 4 },
    { name: 'Chicken wrap', ingredients: ['chicken', 'lettuce', 'tortillas'], servings: 4 },
    { name: 'Beef wraps', ingredients: ['ground beef', 'tomatoes', 'bell peppers', 'tortillas'], servings: 4 },
    { name: 'Deconstructed Tuna Sushi', ingredients: ['tuna', 'rice', 'soy sauce', 'avocado'], servings: 4 },
    { name: 'Deconstructed Salmon Sushi', ingredients: ['salmon', 'rice', 'soy sauce', 'avocado'], servings: 4 },
    { name: 'Rice and Beans with Chicken', ingredients: ['rice', 'beans', 'chicken'], servings: 4 },
    { name: 'Steak and Eggs', ingredients: ['steak', 'eggs', 'potatoes'], servings: 4 },
    { name: 'Avocado Chickpea Sandwich', ingredients: ['chickpeas', 'avocado', 'bread'], servings: 4 },
    { name: 'Quinoa Veggie Bowl', ingredients: ['quinoa', 'zucchini', 'bell peppers', 'carrots'], servings: 4 },
    { name: 'Lentil and Tomato Salad', ingredients: ['lentils', 'tomatoes', 'olive oil'], servings: 4 },
    { name: 'Spinach and Cheese Wrap', ingredients: ['tortilla', 'spinach', 'cheese'], servings: 4 },
    { name: 'Cucumber and Hummus Sandwich', ingredients: ['bread', 'cucumber', 'hummus'], servings: 4 },
    { name: 'Egg and Avocado Wrap', ingredients: ['eggs', 'tortilla', 'avocado'], servings: 4 },
    { name: 'Sweet Potato and Black Bean Bowl', ingredients: ['sweet potatoes', 'black beans', 'olive oil'], servings: 4 },
    { name: 'Tomato and Mozzarella Salad', ingredients: ['tomatoes', 'fresh mozzarella', 'basil'], servings: 4 },
    { name: 'Zucchini and Cheese Quesadilla', ingredients: ['tortilla', 'zucchini', 'cheese'], servings: 4 },
    { name: 'Broccoli and Cheese Sandwich', ingredients: ['bread', 'broccoli', 'cheese'], servings: 4 },
    { name: 'Cauliflower Chickpea Bowl', ingredients: ['cauliflower', 'chickpeas', 'olive oil'], servings: 4 },
    { name: 'Kale and Lentil Wrap', ingredients: ['tortilla', 'kale', 'lentils'], servings: 4 },
    { name: 'Hummus and Veggie Bowl', ingredients: ['hummus', 'cucumbers', 'bell peppers', 'carrots'], servings: 4 },
    { name: 'Avocado and Tomato Sandwich', ingredients: ['bread', 'avocado', 'tomato'], servings: 4 },
    { name: 'Sweet Potato and Spinach Wrap', ingredients: ['tortilla', 'sweet potatoes', 'spinach'], servings: 4 },
    { name: 'Fruit Salad with Yogurt', ingredients: ['yogurt', 'blueberries', 'mango', 'pineapple'], servings: 4 },
    { name: 'Butternut Squash and Chickpea Bowl', ingredients: ['butternut squash', 'chickpeas', 'olive oil'], servings: 4 },
    { name: 'Quinoa and Black Bean Salad', ingredients: ['quinoa', 'black beans', 'bell peppers'], servings: 4 },
    { name: 'Cabbage and Carrot Slaw', ingredients: ['cabbage', 'carrots', 'olive oil'], servings: 4 },
    { name: 'Ramen Veggie Bowl', ingredients: ['ramen noodles', 'carrots', 'peas', 'zucchini'], servings: 4 },
    { name: 'Egg and Cheese Sandwich', ingredients: ['bread', 'eggs', 'cheese'], servings: 4 },
    { name: 'Spinach and Chickpea Salad', ingredients: ['spinach', 'chickpeas', 'olive oil'], servings: 4 },
    { name: 'Tomato, Zucchini, and Cheese Wrap', ingredients: ['tortilla', 'tomatoes', 'zucchini', 'cheese'], servings: 4 },
    { name: 'Avocado and Black Bean Wrap', ingredients: ['tortilla', 'avocado', 'black beans'], servings: 4 },
    { name: 'Potato and Onion Salad', ingredients: ['potatoes', 'onions', 'olive oil'], servings: 4 },
    { name: 'Bell Pepper and Hummus Wrap', ingredients: ['tortilla', 'bell peppers', 'hummus'], servings: 4 },
    { name: 'Cucumber and Avocado Salad', ingredients: ['cucumbers', 'avocado', 'lemon'], servings: 4 },
    { name: 'Mango and Papaya Fruit Bowl', ingredients: ['mango', 'papaya', 'lime'], servings: 4 },
    { name: 'Lentil and Vegetable Soup Bowl', ingredients: ['lentils', 'carrots', 'tomato paste'], servings: 4 },
    { name: 'Broccoli and Chickpea Salad', ingredients: ['broccoli', 'chickpeas', 'olive oil'], servings: 4 },
    { name: 'Chicken Caesar Wrap', ingredients: ['chicken', 'lettuce', 'tortilla', 'salad dressing'], servings: 4 },
    { name: 'Tuna Rice Bowl', ingredients: ['tuna', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Salmon Salad Plate', ingredients: ['salmon', 'lettuce', 'cucumber', 'olive oil'], servings: 4 },
    { name: 'Shrimp Veggie Wrap', ingredients: ['shrimp', 'lettuce', 'tortilla', 'mayo'], servings: 4 },
    { name: 'Ground Beef Rice Bowl', ingredients: ['ground beef', 'rice', 'onions'], servings: 4 },
    { name: 'Chicken Quinoa Bowl', ingredients: ['chicken', 'quinoa', 'bell peppers'], servings: 4 },
    { name: 'Turkey-Style Chicken Sandwich', ingredients: ['chicken breast', 'bread', 'mayonnaise'], servings: 4 },
    { name: 'Steak Salad Bowl', ingredients: ['steak', 'lettuce', 'tomatoes', 'olive oil'], servings: 4 },
    { name: 'Sausage and Pepper Wrap', ingredients: ['sausages', 'bell peppers', 'tortilla'], servings: 4 },
    { name: 'Crab Salad Sandwich', ingredients: ['crab', 'mayonnaise', 'bread'], servings: 4 },
    { name: 'Lobster Roll', ingredients: ['lobster', 'mayonnaise', 'bread'], servings: 4 },
    { name: 'Shrimp Rice Salad', ingredients: ['shrimp', 'rice', 'cucumbers'], servings: 4 },
    { name: 'Salmon Avocado Bowl', ingredients: ['salmon', 'avocado', 'rice'], servings: 4 },
    { name: 'Tuna Pasta Salad', ingredients: ['tuna', 'pasta', 'olive oil'], servings: 4 },
    { name: 'Chicken and Sweet Potato Bowl', ingredients: ['chicken', 'sweet potatoes'], servings: 4 },
    { name: 'Beef and Cabbage Bowl', ingredients: ['ground beef', 'cabbage', 'onions'], servings: 4 },
    { name: 'Sardine Tomato Toast', ingredients: ['sardines', 'tomatoes', 'bread'], servings: 4 },
    { name: 'Spam Fried Rice Bowl', ingredients: ['spam', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Chicken Pesto Sandwich', ingredients: ['chicken', 'pesto', 'bread'], servings: 4 },
    { name: 'Ham and Avocado Wrap', ingredients: ['ham', 'avocado', 'tortilla'], servings: 4 },
    { name: 'Shrimp Quinoa Bowl', ingredients: ['shrimp', 'quinoa', 'bell peppers'], servings: 4 },
    { name: 'Salmon Cucumber Wrap', ingredients: ['salmon', 'cucumber', 'tortilla'], servings: 4 },
    { name: 'Chicken Mushroom Rice Bowl', ingredients: ['chicken', 'mushrooms', 'rice'], servings: 4 },
    { name: 'Beef Tomato Wrap', ingredients: ['ground beef', 'tomato', 'tortilla'], servings: 4 },
    { name: 'Tuna Chickpea Salad', ingredients: ['tuna', 'chickpeas', 'olive oil'], servings: 4 },
    { name: 'Sausage Potato Bowl', ingredients: ['sausages', 'potatoes', 'onions'], servings: 4 },
    { name: 'Chicken Broccoli Salad', ingredients: ['chicken', 'broccoli', 'olive oil'], servings: 4 },
    { name: 'Crab Rice Bowl', ingredients: ['crab', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Lobster Pasta Bowl', ingredients: ['lobster', 'pasta', 'butter'], servings: 4 },
    { name: 'Shrimp Avocado Salad', ingredients: ['shrimp', 'avocado', 'lettuce'], servings: 4 }
  ],
  dinner: [
    { name: 'Chicken and rice', ingredients: ['chicken', 'rice', 'olive oil'], servings: 4 },
    { name: 'Ground beef and rice', ingredients: ['ground beef', 'rice', 'onions'], servings: 4 },
    { name: 'Pasta with tomato sauce', ingredients: ['pasta', 'tomato sauce', 'olive oil'], servings: 4 },
    { name: 'Pasta with butter and cheese', ingredients: ['pasta', 'butter', 'cheese'], servings: 4 },
    { name: 'Sausage and peppers', ingredients: ['sausages', 'bell peppers', 'onions'], servings: 4 },
    { name: 'Beef and potatoes', ingredients: ['ground beef', 'potatoes', 'onions'], servings: 4 },
    { name: 'Chicken stir fry', ingredients: ['chicken', 'bell peppers', 'soy sauce'], servings: 4 },
    { name: 'Lentil stew', ingredients: ['lentils', 'diced tomatoes', 'onions'], servings: 4 },
    { name: 'Chickpea tomato bowl', ingredients: ['chickpeas', 'tomato sauce', 'onions'], servings: 4 },
    { name: 'Quinoa veggie bowl', ingredients: ['quinoa', 'carrots', 'zucchini'], servings: 4 },
    { name: 'Hot dog in tortilla', ingredients: ['hot dogs', 'tortillas', 'ketchup'], servings: 4 },
    { name: 'Spam rice bowl', ingredients: ['spam', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Chili with rice', ingredients: ['chili', 'rice'], servings: 4 },
    { name: 'Chili with bread', ingredients: ['chili', 'bread'], servings: 4 },
    { name: 'Ramen with egg', ingredients: ['ramen noodles', 'eggs'], servings: 4 },
    { name: 'Ramen with vegetables', ingredients: ['ramen', 'carrots', 'peas'], servings: 4 },
    { name: 'Tuna pasta', ingredients: ['pasta', 'canned tuna', 'olive oil'], servings: 4 },
    { name: 'Salmon rice bowl', ingredients: ['canned salmon', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Couscous with vegetables', ingredients: ['couscous', 'zucchini', 'carrots'], servings: 4 },
    { name: 'Quinoa chicken bowl', ingredients: ['quinoa', 'chicken', 'olive oil'], servings: 4 },
    { name: 'Sausage and potatoes', ingredients: ['sausages', 'potatoes', 'onions'], servings: 4 },
    { name: 'Chicken soup bowl', ingredients: ['soup', 'chicken'], servings: 4 },
    { name: 'Beef chili bowl', ingredients: ['chili', 'ground beef'], servings: 4 },
    { name: 'Vegetable stew', ingredients: ['carrots', 'potatoes', 'peas', 'tomato paste'], servings: 4 },
    { name: 'Cabbage stir fry', ingredients: ['cabbage', 'onions', 'soy sauce'], servings: 4 },
    { name: 'Green beans with butter', ingredients: ['green beans', 'butter'], servings: 4 },
    { name: 'Corn and rice bowl', ingredients: ['corn', 'rice', 'butter'], servings: 4 },
    { name: 'Tomato rice', ingredients: ['rice', 'tomato sauce', 'olive oil'], servings: 4 },
    { name: 'Chicken tortilla wrap', ingredients: ['chicken', 'tortillas', 'lettuce'], servings: 4 },
    { name: 'Beef tortilla wrap', ingredients: ['ground beef', 'tortillas', 'onions'], servings: 4 },
    { name: 'Tuna tortilla wrap', ingredients: ['tuna', 'tortillas', 'mayonnaise'], servings: 4 },
    { name: 'Chickpea tortilla wrap', ingredients: ['chickpeas', 'tortillas', 'lettuce'], servings: 4 },
    { name: 'Egg tortilla wrap', ingredients: ['eggs', 'tortillas', 'cheese'], servings: 4 },
    { name: 'Baked Chicken with Broccoli', ingredients: ['chicken', 'broccoli', 'olive oil'], servings: 4 },
    { name: 'Sweet Potato and Sausage Bake', ingredients: ['sweet potatoes', 'sausages', 'onions'], servings: 4 },
    { name: 'Quinoa and Vegetable Stir Fry', ingredients: ['quinoa', 'zucchini', 'carrots', 'bell peppers'], servings: 4 },
    { name: 'Lentil Curry', ingredients: ['lentils', 'coconut milk', 'curry sauce'], servings: 4 },
    { name: 'Chickpea and Tomato Stir Fry', ingredients: ['chickpeas', 'diced tomatoes', 'onions'], servings: 4 },
    { name: 'Mushroom and Zucchini Pasta', ingredients: ['pasta', 'mushrooms', 'zucchini', 'olive oil'], servings: 4 },
    { name: 'Chicken and Butternut Squash Bowl', ingredients: ['chicken', 'butternut squash', 'olive oil'], servings: 4 },
    { name: 'Salmon and Quinoa Bowl', ingredients: ['salmon', 'quinoa', 'spinach'], servings: 4 },
    { name: 'Ground Beef and Potato Skillet', ingredients: ['ground beef', 'potatoes', 'onions'], servings: 4 },
    { name: 'Bell Pepper and Chicken Stir Fry', ingredients: ['chicken', 'bell peppers', 'soy sauce'], servings: 4 },
    { name: 'Cabbage and Sausage Stir Fry', ingredients: ['sausages', 'cabbage', 'onions'], servings: 4 },
    { name: 'Tuna and Rice Bowl', ingredients: ['canned tuna', 'rice', 'bell peppers'], servings: 4 },
    { name: 'Shrimp and Zucchini Pasta', ingredients: ['shrimp', 'pasta', 'zucchini', 'olive oil'], servings: 4 },
    { name: 'Chicken and Green Bean Stir Fry', ingredients: ['chicken', 'green beans', 'olive oil'], servings: 4 },
    { name: 'Vegetable Coconut Curry', ingredients: ['carrots', 'cauliflower', 'coconut milk', 'curry sauce'], servings: 4 },
    { name: 'Eggplant and Tomato Pasta', ingredients: ['eggplant', 'pasta', 'tomato sauce'], servings: 4 },
    { name: 'Chicken and Sweet Potato Quinoa Bowl', ingredients: ['chicken', 'quinoa', 'sweet potatoes'], servings: 4 },
    { name: 'Lentil and Spinach Stew', ingredients: ['lentils', 'spinach', 'tomato paste'], servings: 4 },
    { name: 'Sausage and Bell Pepper Quinoa', ingredients: ['sausages', 'quinoa', 'bell peppers'], servings: 4 },
    { name: 'Broccoli and Cheese Rice Bake', ingredients: ['rice', 'broccoli', 'cheese'], servings: 4 },
    { name: 'Chickpea and Vegetable Coconut Bowl', ingredients: ['chickpeas', 'coconut milk', 'carrots'], servings: 4 },
    { name: 'Ground Beef and Zucchini Skillet', ingredients: ['ground beef', 'zucchini', 'onions'], servings: 4 },
    { name: 'Cabbage and Carrot Stir Fry', ingredients: ['cabbage', 'carrots', 'olive oil'], servings: 4 },
    { name: 'Chicken and Tomato Rice Bowl', ingredients: ['chicken', 'rice', 'diced tomatoes'], servings: 4 },
    { name: 'Salmon and Vegetable Stir Fry', ingredients: ['salmon', 'zucchini', 'bell peppers', 'olive oil'], servings: 4 },
    { name: 'Quinoa and Spinach Bowl', ingredients: ['quinoa', 'spinach', 'mushrooms'], servings: 4 },
    { name: 'Sweet Potato and Lentil Stew', ingredients: ['sweet potatoes', 'lentils', 'onions'], servings: 4 },
    { name: 'Eggplant and Chickpea Curry', ingredients: ['eggplant', 'chickpeas', 'curry sauce'], servings: 4 },
    { name: 'Shrimp and Vegetable Rice Bowl', ingredients: ['shrimp', 'rice', 'bell peppers', 'carrots'], servings: 4 },
    { name: 'Chicken and Butternut Squash Soup', ingredients: ['chicken', 'butternut squash', 'chicken broth'], servings: 4 },
    { name: 'Air Fryer Chicken Thighs with Potatoes', ingredients: ['chicken thighs', 'potatoes', 'olive oil'], servings: 4 },
    { name: 'Grilled Steak with Asparagus', ingredients: ['steak', 'asparagus', 'olive oil'], servings: 4 },
    { name: 'Air Fryer Sausage and Brussels Sprouts', ingredients: ['sausages', 'brussels sprouts'], servings: 4 },
    { name: 'Grilled Salmon with Lemon Zucchini', ingredients: ['salmon', 'zucchini', 'lemon'], servings: 4 },
    { name: 'Air Fryer Chicken Tenders with Sweet Potatoes', ingredients: ['chicken tenders', 'sweet potatoes'], servings: 4 },
    { name: 'Grilled Shrimp Skewers', ingredients: ['shrimp', 'bell peppers', 'onions'], servings: 4 },
    { name: 'Grilled Chicken Breast with Pineapple', ingredients: ['chicken breast', 'pineapple'], servings: 4 },
    { name: 'Grilled Lobster Tails', ingredients: ['lobster', 'butter', 'lemon'], servings: 4 },
    { name: 'Grilled Sausage and Onion Platter', ingredients: ['sausages', 'onions', 'peppers'], servings: 4 },
    { name: 'Grilled Spam Crisps with Rice', ingredients: ['spam', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Grilled Chicken and Avocado Plate', ingredients: ['chicken', 'avocado', 'lemon'], servings: 4 },
    { name: 'Grilled Salmon Bites with Rice', ingredients: ['salmon', 'rice', 'soy sauce'], servings: 4 },
    { name: 'Grilled Shrimp with Corn and Zucchini', ingredients: ['shrimp', 'corn', 'zucchini'], servings: 4 },
    { name: 'Grilled Chicken Wings with Hot Sauce', ingredients: ['chicken wings', 'hot sauce'], servings: 4 },
    { name: 'Grilled Steak and Tomato Salad', ingredients: ['steak', 'tomatoes', 'lettuce'], servings: 4 },
    { name: 'Grilled Scallops with Buttered Rice', ingredients: ['scallops', 'rice', 'butter'], servings: 4 },
    { name: 'Grilled Snapper with Mango Salsa', ingredients: ['snapper', 'mango', 'lime'], servings: 4 },
    { name: 'Grilled Sausage Coins with Potatoes', ingredients: ['sausages', 'potatoes'], servings: 4 },
    { name: 'Grilled Chicken Thighs with Cabbage Slaw', ingredients: ['chicken thighs', 'cabbage', 'carrots'], servings: 4 },
    { name: 'Grilled Shrimp and Broccoli Bowl', ingredients: ['shrimp', 'broccoli', 'rice'], servings: 4 },
    { name: 'Grilled Ham Steaks with Pineapple', ingredients: ['ham', 'pineapple'], servings: 4 },
    { name: 'Grilled Crab Cakes', ingredients: ['crab', 'breadcrumbs', 'mayonnaise'], servings: 4 },
    { name: 'Grilled Chicken and Zucchini Skewers', ingredients: ['chicken', 'zucchini'], servings: 4 },
    { name: 'Grilled Cod Fish Bites', ingredients: ['cod', 'breadcrumbs'], servings: 4 },
    { name: 'Grilled Sausage with Sweet Potato Coins', ingredients: ['sausages', 'sweet potatoes'], servings: 4 },
    { name: 'Grilled Chicken and Zucchini Skewers', ingredients: ['chicken', 'zucchini'], servings: 4 },
    { name: 'Air Fryer Chicken Parmesan Bites', ingredients: ['chicken', 'breadcrumbs', 'cheese', 'pasta sauce'], servings: 4 },
    { name: 'Grilled Shrimp and Avocado Bowl', ingredients: ['shrimp', 'avocado', 'rice'], servings: 4 },
    { name: 'Air Fryer Salmon with Brussels Sprouts', ingredients: ['salmon', 'brussels sprouts'], servings: 4 },
    { name: 'Grilled Steak and Mushroom Plate', ingredients: ['steak', 'mushrooms'], servings: 4 },
    { name: 'Grilled Lobster Tails', ingredients: ['lobster', 'butter', 'lemon'], servings: 4 }
  ]
};

// Check if an ingredient is available in inventory (flexible matching)
const hasIngredient = (ingredientName, inventoryItems) => {
  const searchTerm = ingredientName?.toLowerCase()?.trim();
  return inventoryItems?.some(item => {
    const itemName = item?.name?.toLowerCase()?.trim();
    // Exact match or inventory item contains ingredient name
    return itemName === searchTerm || 
           itemName?.includes(searchTerm);
  });
};

// Check if ALL required ingredients are available for a meal
const canMakeMeal = (meal, inventoryItems) => {
  return meal?.ingredients?.every(ingredient => 
    hasIngredient(ingredient, inventoryItems)
  );
};

// Generate a meal suggestion based on available ingredients and cooking equipment
const generateMeal = (mealType, inventoryItems, tripData = null) => {
  const mealTypeKey = mealType?.toLowerCase();
  let availableMeals = MEAL_BANK?.[mealTypeKey] || [];
  
  // Filter dinner meals based on cooking equipment
  if (mealTypeKey === 'dinner' && tripData) {
    availableMeals = availableMeals?.filter(meal => {
      const mealName = meal?.name || '';
      
      // If meal starts with "Grilled", only include if user has Grill
      if (mealName?.startsWith('Grilled')) {
        return tripData?.hasGrill === true;
      }
      
      // If meal starts with "Air Fryer", only include if user has Air Fryer
      if (mealName?.startsWith('Air Fryer')) {
        return tripData?.hasAirFryer === true;
      }
      
      // All other dinner meals are always available
      return true;
    });
  }
  
  // Filter meals where ALL ingredients are available
  const makableMeals = availableMeals?.filter(meal => canMakeMeal(meal, inventoryItems));
  
  if (makableMeals?.length === 0) {
    return null;
  }
  
  // Select a random meal from available options
  const randomMeal = makableMeals?.[Math.floor(Math.random() * makableMeals?.length)];
  
  return {
    name: randomMeal?.name,
    servings: randomMeal?.servings,
    ingredients: randomMeal?.ingredients?.map(ing => ({
      name: ing,
      required: 1,
      unit: 'unit',
      available: 1
    }))
  };
};

const MealPlanning = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [meals, setMeals] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [completedDays, setCompletedDays] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const tripResult = await tripService?.getActiveTrip(user?.id);
        const tripData = tripResult?.data;
        
        if (!tripData) {
          setTrip(null);
          setInventoryItems([]);
          setMeals([]);
          setLoading(false);
          return;
        }
        
        setTrip(tripData);
        setCompletedDays(tripData?.completedDays || []);
        
        const inventoryResult = await inventoryService?.getInventoryByTrip(tripData?.id);
        setInventoryItems(inventoryResult?.data || []);
        
        const mealsResult = await mealService?.getMealsByTrip(tripData?.id);
        if (mealsResult?.data && mealsResult?.data?.length > 0) {
          // Group meals by day
          const mealsByDay = {};
          mealsResult?.data?.forEach(meal => {
            if (!mealsByDay?.[meal?.day]) {
              mealsByDay[meal?.day] = { dayNumber: meal?.day, meals: {} };
            }
            const mealTypeUI = mapDBTypeToUIType(meal?.type)?.toLowerCase();
            mealsByDay[meal?.day].meals[mealTypeUI] = {
              id: meal?.id,
              name: meal?.mealName,
              servings: meal?.servings || 4,
              ingredients: meal?.ingredients || [],
              isLeftover: meal?.isLeftover
            };
          });
          
          // Filter out completed days
          const activeMeals = Object.values(mealsByDay)?.filter(
            day => !tripData?.completedDays?.includes(day?.dayNumber)
          );
          setMeals(activeMeals);
        } else {
          // Generate initial meal plan
          const generatedMeals = [];
          for (let day = 1; day <= tripData?.duration; day++) {
            if (!tripData?.completedDays?.includes(day)) {
              generatedMeals?.push({
                dayNumber: day,
                meals: {
                  breakfast: generateMeal('Breakfast', inventoryResult?.data || [], tripData),
                  lunch: generateMeal('Lunch', inventoryResult?.data || [], tripData),
                  dinner: generateMeal('Dinner', inventoryResult?.data || [], tripData)
                }
              });
            }
          }
          setMeals(generatedMeals);
        }
        
        const profileResult = await userProfileService?.getProfile(user?.id);
        setUserProfile(profileResult?.data);
      } catch (e) {
        setError(e?.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const handleRefreshMeal = async (dayNumber, mealType) => {
    // Get the current meal to exclude it from selection
    const currentDay = meals?.find(day => day?.dayNumber === dayNumber);
    const currentMeal = currentDay?.meals?.[mealType?.toLowerCase()];
    
    // Generate new meal
    let newMeal = generateMeal(mealType, inventoryItems, trip);
    
    if (newMeal) {
      // Update meal plan
      const updatedMeals = meals?.map(day => {
        if (day?.dayNumber === dayNumber) {
          return {
            ...day,
            meals: {
              ...day?.meals,
              [mealType?.toLowerCase()]: newMeal
            }
          };
        }
        return day;
      });
      
      setMeals(updatedMeals);
      
      // Save to database
      if (trip?.id && currentMeal?.id) {
        await mealService?.updateMeal(currentMeal?.id, {
          mealName: newMeal?.name,
          ingredients: newMeal?.ingredients
        });
      }
    }
  };

  const handleRandomizeSnack = () => {
    // Filter inventory for snacks only
    const snacks = inventoryItems?.filter(item => {
      const category = item?.category?.toLowerCase();
      return (category === 'snack' || category === 'snacks') && item?.quantity > 0;
    });
    
    if (snacks?.length === 0) {
      setError({ error: 'No snacks available in inventory' });
      return;
    }
    
    // Select random snack and display it
    const randomSnack = snacks?.[Math.floor(Math.random() * snacks?.length)];
    setError(randomSnack);
  };

  const handleSelectSnack = async () => {
    if (!error || error?.error) return;
    
    try {
      // Update inventory quantity
      await inventoryService?.updateInventoryItem(error?.id, {
        itemName: error?.name,
        category: error?.category,
        quantity: Math.max(0, error?.quantity - 1),
        unit: error?.unit
      });
      
      // Reload inventory
      const inventoryResult = await inventoryService?.getInventoryByTrip(trip?.id);
      setInventoryItems(inventoryResult?.data || []);
      
      // Clear suggested snack
      setError(null);
    } catch (error) {
      console.error('Error selecting snack:', error);
      setError({ error: 'Failed to select snack. Please try again.' });
    }
  };

  const handleDownloadPDF = async () => {
    if (meals?.length === 0) {
      setError({ error: 'No meal plan to download' });
      return;
    }
    
    try {
      const doc = new jsPDF();
      
      // Add title
      doc?.setFontSize(20);
      doc?.setFont(undefined, 'bold');
      doc?.text(`${trip?.tripName} - Meal Plan`, 14, 20);
      
      // Add trip details
      doc?.setFontSize(10);
      doc?.setFont(undefined, 'normal');
      doc?.setTextColor(100, 100, 100);
      doc?.text(`Duration: ${trip?.duration} days | Crew Size: ${trip?.crewSize}`, 14, 28);
      
      let yPosition = 40;
      
      // Add meals for each day
      meals?.forEach((day, dayIndex) => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc?.addPage();
          yPosition = 20;
        }
        
        // Day header
        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.setTextColor(0, 0, 0);
        doc?.text(`Day ${day?.dayNumber}`, 14, yPosition);
        yPosition += 8;
        
        // Meals for the day
        ['breakfast', 'lunch', 'dinner']?.forEach((mealType, mealIndex) => {
          const meal = day?.meals?.[mealType];
          
          if (meal) {
            // Meal type header
            doc?.setFontSize(12);
            doc?.setFont(undefined, 'bold');
            doc?.setTextColor(50, 50, 50);
            doc?.text(`${mealType?.charAt(0)?.toUpperCase() + mealType?.slice(1)}:`, 20, yPosition);
            yPosition += 6;
            
            // Meal name
            doc?.setFontSize(10);
            doc?.setFont(undefined, 'normal');
            doc?.setTextColor(0, 0, 0);
            doc?.text(meal?.name, 20, yPosition);
            yPosition += 5;
            
            // Servings
            doc?.setFontSize(9);
            doc?.setTextColor(80, 80, 80);
            doc?.text(`Servings: ${meal?.servings}`, 20, yPosition);
            yPosition += 5;
            
            // Ingredients
            if (meal?.ingredients?.length > 0) {
              doc?.setFontSize(9);
              doc?.setFont(undefined, 'bold');
              doc?.text('Ingredients:', 20, yPosition);
              yPosition += 4;
              
              meal?.ingredients?.forEach((ingredient) => {
                // Check if we need a new page
                if (yPosition > 270) {
                  doc?.addPage();
                  yPosition = 20;
                }
                
                doc?.setFontSize(8);
                doc?.setTextColor(80, 80, 80);
                const ingredientText = `  • ${ingredient?.name}${ingredient?.required ? ` (${ingredient?.required} ${ingredient?.unit || 'unit'}${ingredient?.required > 1 ? 's' : ''})` : ''}`;
                doc?.text(ingredientText, 24, yPosition);
                yPosition += 4;
              });
              yPosition += 2;
            }
          }
          
          // Add spacing between meals
          if (mealIndex < 2) {
            yPosition += 2;
          }
        });
        
        // Add spacing between days
        if (dayIndex < meals?.length - 1) {
          yPosition += 6;
          // Add separator line between days
          doc?.setDrawColor(220, 220, 220);
          doc?.line(14, yPosition - 3, 196, yPosition - 3);
        }
      });
      
      // Add footer to all pages
      const pageCount = doc?.internal?.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc?.setPage(i);
        doc?.setFontSize(8);
        doc?.setTextColor(150, 150, 150);
        doc?.text(
          `Generated by GalleyPlan - Page ${i} of ${pageCount}`,
          105,
          doc?.internal?.pageSize?.height - 10,
          { align: 'center' }
        );
      }
      
      // Save the PDF
      doc?.save(`${trip?.tripName?.replace(/\s+/g, '_')}_Meal_Plan.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError({ error: 'Failed to generate PDF. Please try again.' });
    }
  };

  const handleMarkDayComplete = async (dayNumber) => {
    const updatedCompletedDays = [...completedDays, dayNumber];
    setCompletedDays(updatedCompletedDays);
    
    // Remove the completed day from meals display
    const updatedMeals = meals?.filter(day => day?.dayNumber !== dayNumber);
    setMeals(updatedMeals);
    
    // Persist to database
    if (trip?.id) {
      const { error: updateError } = await tripService?.updateCompletedDays(trip?.id, updatedCompletedDays);
      if (updateError) {
        console.error('Error saving completed day:', updateError);
        // Revert on error
        setCompletedDays(completedDays);
        // Restore the day to meals
        setMeals(meals);
      }
    }
  };

  const handleUndoDayComplete = async (dayNumber) => {
    const updatedCompletedDays = completedDays?.filter(day => day !== dayNumber);
    setCompletedDays(updatedCompletedDays);
    
    // Regenerate meals for the restored day
    const newDayMeals = {
      dayNumber: dayNumber,
      meals: {
        breakfast: generateMeal('Breakfast', inventoryItems, trip),
        lunch: generateMeal('Lunch', inventoryItems, trip),
        dinner: generateMeal('Dinner', inventoryItems, trip)
      }
    };
    
    // Add the day back to meals in correct order
    const updatedMeals = [...meals, newDayMeals]?.sort((a, b) => a?.dayNumber - b?.dayNumber);
    setMeals(updatedMeals);
    
    // Persist to database
    if (trip?.id) {
      const { error: updateError } = await tripService?.updateCompletedDays(trip?.id, updatedCompletedDays);
      if (updateError) {
        console.error('Error undoing completed day:', updateError);
        // Revert on error
        setCompletedDays(completedDays);
        // Remove the day from meals again
        setMeals(meals);
      }
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading meal plan...</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  if (!trip) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-warning)" className="mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No Active Trip</h2>
            <p className="text-muted-foreground mb-6">Please create a trip to start meal planning.</p>
            <Button variant="default" onClick={() => navigate('/trip-setup')}>
              Create Trip
            </Button>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-8 pb-6">
          <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-6 mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-4">Meal Planning</h1>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                <Icon name="Ship" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-foreground">{trip?.tripName}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                <Icon name="Users" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-foreground">{trip?.crewSize} crew</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                <Icon name="Calendar" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-foreground">{trip?.duration} days</span>
              </div>
            </div>
          </div>

          {/* Download and Snack Randomizer Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Download Meal Plan Button */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-6">
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Download Meal Plan</h2>
                  <p className="text-sm text-muted-foreground">Download a PDF version of your meal plan for offline access during your trip</p>
                </div>
                <Button
                  onClick={handleDownloadPDF}
                  iconName="Download"
                  iconSize={18}
                  className="whitespace-nowrap"
                >
                  Download Meal Plan
                </Button>
              </div>
            </div>

            {/* Snack Randomizer Section */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Snack Randomizer</h2>
                  <p className="text-sm text-muted-foreground">Get a random snack suggestion from your available inventory</p>
                </div>
                <Button
                  onClick={handleRandomizeSnack}
                  iconName="Shuffle"
                  iconSize={18}
                  className="whitespace-nowrap"
                >
                  Randomize Snack
                </Button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  {error?.error ? (
                    <div className="flex items-center gap-2 text-warning">
                      <Icon name="AlertCircle" size={20} />
                      <span className="text-sm font-medium">{error?.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Cookie" size={24} color="var(--color-primary)" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">{error?.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Icon name="Package" size={14} color="var(--color-muted-foreground)" />
                            <span className="text-sm text-muted-foreground">
                              {error?.quantity} {error?.unit} available
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSelectSnack}
                          iconName="Check"
                          iconSize={16}
                          className=""
                        >
                          Select Snack
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setError(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Show completed days section with undo option */}
            {completedDays?.length > 0 && (
              <div className="bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden">
                <div className="bg-muted px-6 py-4">
                  <h2 className="text-xl font-semibold text-foreground">Completed Days</h2>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-3">
                    {completedDays?.sort((a, b) => a - b)?.map((dayNumber) => (
                      <div key={dayNumber} className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                        <span className="text-sm font-medium text-foreground">Day {dayNumber}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleUndoDayComplete(dayNumber)}
                          iconName="Undo2"
                          iconSize={14}
                          className="ml-2 h-6 w-6"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Click the undo button to restore a completed day and regenerate its meal plan.
                  </p>
                </div>
              </div>
            )}

            {meals?.map((day) => {
              return (
                <div key={day?.dayNumber} className="bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden">
                  <div className="bg-primary px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-foreground">Day {day?.dayNumber}</h2>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMarkDayComplete(day?.dayNumber)}
                        iconName="CheckCircle"
                        iconSize={16}
                        className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                      >
                        Mark Day Complete
                      </Button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {['breakfast', 'lunch', 'dinner']?.map((mealType) => {
                        const meal = day?.meals?.[mealType];

                        return (
                          <div key={mealType} className="bg-muted rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Icon 
                                  name={mealType === 'breakfast' ? 'Coffee' : mealType === 'lunch' ? 'Sandwich' : 'UtensilsCrossed'} 
                                  size={20} 
                                  color="var(--color-primary)" 
                                />
                                <h3 className="text-lg font-semibold text-foreground capitalize">{mealType}</h3>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRefreshMeal(day?.dayNumber, mealType?.charAt(0)?.toUpperCase() + mealType?.slice(1))}
                                iconName="RefreshCw"
                                iconSize={16}
                              />
                            </div>

                            {meal ? (
                              <>
                                <h4 className="font-medium text-foreground mb-2">
                                  {meal?.name}
                                  {meal?.isLeftover && <span className="text-xs text-warning ml-2">(Leftover)</span>}
                                </h4>
                                <div className="text-sm text-muted-foreground mb-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Icon name="Users" size={14} />
                                    <span>{meal?.servings} servings</span>
                                  </div>
                                  {meal?.ingredients?.length > 0 && (
                                    <div className="space-y-1">
                                      {meal?.ingredients?.slice(0, 3)?.map((ing, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                          <Icon 
                                            name={ing?.available >= ing?.required ? 'CheckCircle2' : 'AlertCircle'} 
                                            size={12} 
                                            color={ing?.available >= ing?.required ? 'var(--color-success)' : 'var(--color-warning)'}
                                          />
                                          <span className="text-xs">{ing?.name}</span>
                                        </div>
                                      ))}
                                      {meal?.ingredients?.length > 3 && (
                                        <span className="text-xs text-muted-foreground">+{meal?.ingredients?.length - 3} more</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-sm text-muted-foreground">No meal planned</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            
          </div>
        </div>
      </div>
      <MobileNavigation />
    </>
  );
};

export default MealPlanning;